import { inject, Injectable } from '@angular/core';
import { from, map } from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import {
  GetCommunityAdminSampleGQL,
  GetCommunityMembersGQL,
  type Community_Members_With_ProfileFilter,
} from '../graphql/generated/services';
import type {
  CommunityMemberRow,
  CommunityMembersPage,
} from '../models/community-member.model';

const MEMBERS_PER_PAGE = 20;

function buildMembersFilter(
  communityId: string,
  searchQuery: string,
): Community_Members_With_ProfileFilter {
  const communityEq = { community_id: { eq: communityId } };
  const trimmed = searchQuery.trim();
  if (!trimmed) {
    return communityEq;
  }
  // notices の title 検索と同じ（trim 済み語を ilike）。
  return {
    and: [communityEq, { display_name: { ilike: `%${trimmed}%` } }],
  };
}

@Injectable({ providedIn: 'root' })
export class CommunityMembersService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);
  private readonly getCommunityMembersGql = inject(GetCommunityMembersGQL);
  private readonly getCommunityAdminSampleGql = inject(
    GetCommunityAdminSampleGQL,
  );

  /**
   * メンバー一覧（keyset）。`community_members_with_profile` で表示名・アバターを JOIN。
   * 検索は display_name の ilike のみ（email はコミュニティ内で出さない）。
   */
  fetchMembersPage(
    communityId: string,
    options?: {
      after?: string | null;
      first?: number;
      searchQuery?: string | null;
    },
  ) {
    const first = options?.first ?? MEMBERS_PER_PAGE;
    const after = options?.after ?? null;
    const searchQuery = options?.searchQuery ?? '';
    return this.getCommunityMembersGql
      .fetch({
        variables: {
          first,
          ...(after ? { after } : {}),
          filter: buildMembersFilter(communityId, searchQuery),
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          const collection =
            result.data?.community_members_with_profileCollection;
          const edges = collection?.edges ?? [];
          const pageInfo = collection?.pageInfo;
          const members = edges
            .map((e) => e?.node)
            .filter(
              (
                n,
              ): n is NonNullable<typeof n> & {
                nodeId: string;
                user_id: string;
                role: string;
              } => Boolean(n?.nodeId && n?.user_id && n?.role),
            )
            .map((n) => {
              const trimmed = n.display_name?.trim() || null;
              return {
                nodeId: n.nodeId,
                userId: n.user_id,
                role: n.role,
                displayName: trimmed,
                avatarPath: n.avatar_path ?? null,
                avatarUpdatedAt:
                  n.avatar_updated_at != null
                    ? String(n.avatar_updated_at)
                    : null,
              } satisfies CommunityMemberRow;
            });
          return {
            members,
            hasNextPage: pageInfo?.hasNextPage ?? false,
            endCursor: pageInfo?.endCursor ?? null,
          } satisfies CommunityMembersPage;
        }),
      );
  }

  /** Revoke admin 可否。全件ロードせず admin が2人以上かだけ見る。 */
  fetchHasMultipleAdmins(communityId: string) {
    return this.getCommunityAdminSampleGql
      .fetch({
        variables: { community_id: communityId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          const edges = result.data?.community_membersCollection?.edges ?? [];
          return edges.filter((e) => e?.node?.user_id).length >= 2;
        }),
      );
  }

  /** 管理者が member を admin に昇格、または admin を member に降格する。 */
  updateMemberRole(
    communityId: string,
    userId: string,
    role: 'admin' | 'member',
  ) {
    return from(
      this.supabase.rpc('update_community_member_role', {
        p_community_id: communityId,
        p_user_id: userId,
        p_role: role,
      }),
    ).pipe(
      map(({ error }) => {
        if (error) throw new Error(error.message);
      }),
    );
  }
}
